/**
 * TanStack Query hooks for the review-request queue.
 *
 * Keys are built by {@link reviewRequestKeys} rather than written inline: a key assembled
 * ad-hoc at two call sites is two caches, so the drawer badge and the page would each
 * fetch and a decision would invalidate only one of them. Every key is a plain array of
 * primitives and one params object, which react-query hashes with sorted keys — an inline
 * literal is stable, a value computed per render (a new array, a `Date.now()`) is not.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@/hooks/use-data';
import { reviewRequestService, type ReviewRequestListParams } from '@/services/review-request-service';

const STALE = 30 * 1000;

export const reviewRequestKeys = {
	all: queryKeys.reviewRequests.all,
	list: (params: ReviewRequestListParams) => [...reviewRequestKeys.all, 'list', params] as const,
	detail: (id: string) => [...reviewRequestKeys.all, 'detail', id] as const,
	counts: queryKeys.reviewRequests.counts,
	preview: (id: string) => [...reviewRequestKeys.all, 'preview', id] as const,
};

/**
 * The before → after diff for a writing kind, against LIVE values.
 *
 * `staleTime: 0` — the whole point is to show what is on file at the moment of deciding, so
 * every open refetches rather than trusting a minute-old read.
 */
export function useReviewRequestPreview(id: string, options: { enabled?: boolean } = {}) {
	return useQuery({
		queryKey: reviewRequestKeys.preview(id),
		queryFn: () => reviewRequestService.preview(id),
		enabled: options.enabled ?? true,
		staleTime: 0,
	});
}

/**
 * The `= {}` default builds a new object every render, which LOOKS like the unstable-key
 * bug and is not one: react-query hashes keys structurally (`hashKey` stringifies with
 * sorted object keys), so two distinct empty objects hash identically and no refetch
 * results. What WOULD break is a key containing a value that differs by identity AND
 * content — a `Date.now()`, a freshly-derived array. Don't add one.
 *
 * `placeholderData` is what makes it SAFE to put the selected status and page in the key.
 * Without it, every chip click and every page step unmounts the table into a spinner, which
 * is the blank-the-screen-while-filtering failure CLAUDE.md names. With it the previous
 * rows stay on screen and `isFetching` is the only thing that changes.
 */
export function useReviewRequests(params: ReviewRequestListParams = {}, options: { enabled?: boolean } = {}) {
	return useQuery({
		queryKey: reviewRequestKeys.list(params),
		queryFn: () => reviewRequestService.list(params),
		enabled: options.enabled ?? true,
		staleTime: STALE,
		placeholderData: (previous) => previous,
	});
}

/**
 * The nav badge's numbers.
 *
 * Its own `/count` endpoint rather than a `limit: 1` list read — the badge renders on every
 * screen the drawer is mounted on and must not drag a page of rows behind it.
 *
 * `badge` is `pending + failed`, and the two are kept separate in the payload on purpose.
 * A failed request is finished as far as its status goes and is the one most likely to be
 * forgotten: nothing else on any screen mentions it, so leaving it out of the badge would
 * mean an action a person approved, that then failed, is visible nowhere at all.
 */
export function useReviewRequestCounts(options: { enabled?: boolean } = {}) {
	const query = useQuery({
		queryKey: reviewRequestKeys.counts(),
		queryFn: () => reviewRequestService.counts(),
		enabled: options.enabled ?? true,
		staleTime: STALE,
	});
	const counts = query.data;
	return {
		...query,
		counts,
		badge: (counts?.pending ?? 0) + (counts?.failed ?? 0),
	};
}

/**
 * Invalidate the queue AND everything a handler might have changed.
 *
 * An approved request runs a registered action whose reach this hook cannot know — the
 * registry is keyed by `kind` and a future kind may touch accounts or the ledger. Casting
 * a wide net costs a refetch; casting a narrow one leaves a stale figure on screen that a
 * person then acts on.
 */
function useInvalidateReviewRequests() {
	const qc = useQueryClient();
	return () => {
		void qc.invalidateQueries({ queryKey: reviewRequestKeys.all });
		void qc.invalidateQueries({ queryKey: ['accounting'] });
	};
}

/**
 * Settle a request stuck in `approving`.
 *
 * Same wide invalidation as deciding: one of the two outcomes records that the approved
 * action DID run, so anything downstream of it is as stale as it is after an approval.
 */
export function useSettleReviewRequest() {
	const invalidate = useInvalidateReviewRequests();
	return useMutation({
		mutationFn: ({ id, outcome, note }: { id: string; outcome: 'completed' | 'failed'; note: string }) =>
			reviewRequestService.settle(id, outcome, note),
		onSuccess: () => invalidate(),
	});
}

export function useDecideReviewRequest() {
	const invalidate = useInvalidateReviewRequests();
	return useMutation({
		mutationFn: ({ id, approve, note, previewToken }: { id: string; approve: boolean; note?: string; previewToken?: string | null }) =>
			reviewRequestService.decide(id, approve, note, previewToken),
		// Invalidates on success EVEN when `outcome.status === 'failed'`: the row moved to
		// `failed` and the queue on screen still shows it pending. A failed handler is
		// precisely when the page must not keep claiming the old state.
		onSuccess: () => invalidate(),
	});
}
